/**
 * Custom day cell component for the events calendar
 * Displays a badge with event count for days that have events
 */

import { Badge } from '@mui/material'
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay'
import { CalendarEventDto } from '@/api/types/calendarEvent'
import { isSameDay, parseISO } from 'date-fns'

interface EventDayCellProps extends PickersDayProps {
  events: CalendarEventDto[]
}

/**
 * Custom day cell that shows a badge for days with events
 */
export function EventDayCell(props: EventDayCellProps) {
  const { events, day, outsideCurrentMonth, ...other } = props

  // Count events on this day
  const eventsOnDay = events.filter((event) => {
    const eventDate = parseISO(event.startDateTime)
    return isSameDay(eventDate, day as Date)
  })

  const eventCount = eventsOnDay.length
  const hasEvents = eventCount > 0

  return (
    <Badge
      key={day.toString()}
      overlap="circular"
      badgeContent={hasEvents ? eventCount : undefined}
      color="primary"
      variant={eventCount > 1 ? 'standard' : 'dot'}
      sx={{
        '& .MuiBadge-badge': {
          fontSize: '0.65rem',
          height: eventCount > 1 ? 16 : 8,
          minWidth: eventCount > 1 ? 16 : 8,
          padding: eventCount > 1 ? '0 4px' : 0,
        },
      }}
    >
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        sx={{
          ...(hasEvents && !outsideCurrentMonth && {
            backgroundColor: 'primary.light',
            color: 'primary.contrastText',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: 'primary.main',
            },
            '&.Mui-selected': {
              backgroundColor: 'primary.dark',
            },
          }),
        }}
      />
    </Badge>
  )
}
